"use client";

import Link from "next/link";
import { useState } from "react";

import { track } from "@/lib/analytics/track";
import CheckoutPayButton from "@/components/site/checkout/CheckoutPayButton";
import { Icon } from "@/components/site/Icons";

export interface PickerPackage {
  key: string;
  name: string;
  priceLabel: string;
  /** Present only when the price is confirmed. */
  amount: number | null;
  best: string;
  bullets: string[];
}

export interface PickerAddOn {
  key: string;
  name: string;
  priceLabel: string;
  /** True when the monthly price is confirmed, so the "/ month" suffix applies. */
  confirmed: boolean;
  /** Monthly amount when the price is confirmed. */
  amount: number | null;
  description: string;
}

function usd(amount: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(amount);
}

const contactHref = "/contact?offer=local";

export default function PackagePicker({
  packages,
  addOns,
  checkoutUrl = null,
  checkoutApi = false,
}: {
  packages: PickerPackage[];
  addOns: PickerAddOn[];
  /** Legacy single checkout link, used when Stripe price IDs are not configured. */
  checkoutUrl?: string | null;
  /** When true, the pay button creates a Checkout Session from the current selection. */
  checkoutApi?: boolean;
}) {
  const [pkgKey, setPkgKey] = useState(packages[0]?.key ?? "");
  const [addOnKey, setAddOnKey] = useState<string | null>(addOns[0]?.key ?? null);
  const [payFull, setPayFull] = useState(false);

  const chosen = packages.find((pkg) => pkg.key === pkgKey) ?? packages[0];
  const addOn = addOns.find((item) => item.key === addOnKey) ?? null;
  if (!chosen) return null;

  const due = chosen.amount === null ? null : payFull ? chosen.amount : chosen.amount / 2;
  const dueLabel = due === null ? "TBD" : usd(due);
  const payLabel = due === null ? (payFull ? "Pay in full" : "Pay deposit") : payFull ? `Pay ${dueLabel}` : `Pay ${dueLabel} deposit`;
  const packageId = chosen.key === "starter" || chosen.key === "pro" ? chosen.key : null;
  const addOnId = addOn?.key === "care" || addOn?.key === "care-ai" ? addOn.key : null;
  const apiReady = checkoutApi && packageId !== null && due !== null && (addOn === null || addOnId !== null);
  const canCheckout = (apiReady || Boolean(checkoutUrl)) && due !== null;
  const note = payFull
    ? `Paid in full today.${addOn ? " Monthly care billing starts at launch, on the payment method you use today." : ""}`
    : `The other 50% is due at launch.${addOn ? " Monthly care billing starts at launch, on the payment method you use today." : ""}`;

  return (
    <div className="row" style={{ gap: 32, alignItems: "flex-start" }}>
      <div className="stack g24 min-w-0" style={{ flex: "999 1 560px" }}>
        <div className="stack g12" role="radiogroup" aria-label="Package">
          {packages.map((pkg) => {
            const on = pkg.key === chosen.key;
            return (
              <button
                key={pkg.key}
                className={on ? "opt on" : "opt"}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => {
                  if (pkg.key === chosen.key) return;
                  setPkgKey(pkg.key);
                  const due =
                    pkg.amount === null ? 0 : payFull ? pkg.amount : pkg.amount / 2;
                  track("package_select", {
                    package: pkg.key,
                    add_on: addOnKey ?? "",
                    price_total: due,
                  });
                }}
                style={{ padding: 24 }}
              >
                <span className="radio" aria-hidden="true" />
                <span className="stack g12 flex-1">
                  <span className="row justify-between gap-3">
                    <span className="display text-2xl font-bold text-white">{pkg.name}</span>
                    <span className="price price-sm">{pkg.priceLabel}</span>
                  </span>
                  {pkg.best ? (
                    <span className="small" style={{ color: "#cbd5e1" }}>
                      {pkg.best}
                    </span>
                  ) : null}
                  <span className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                    {pkg.bullets.map((bullet) => (
                      <span key={bullet} className="relative pl-[30px] text-sm leading-[22px] text-slate-200">
                        <span
                          aria-hidden="true"
                          className="absolute left-0 top-px inline-flex h-5 w-5 items-center justify-center rounded-md bg-[rgba(10,179,184,0.1)] text-[#0ab3b8]"
                        >
                          <Icon size={12} strokeWidth={3} d="M20 6 9 17l-5-5" />
                        </span>
                        {bullet}
                      </span>
                    ))}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="stack g12">
          <h3 className="h4">Add-ons</h3>
          {addOns.map((item) => {
            const on = item.key === addOnKey;
            return (
              <button
                key={item.key}
                className={on ? "opt on" : "opt"}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => {
                  const next = on ? null : item.key;
                  setAddOnKey(next);
                  const due = chosen.amount === null ? 0 : payFull ? chosen.amount : chosen.amount / 2;
                  track("package_select", {
                    package: chosen.key,
                    add_on: next ?? "",
                    price_total: due,
                  });
                }}
              >
                <span className="box" aria-hidden="true" />
                <span className="stack g8 flex-1">
                  <span className="row justify-between gap-3">
                    <span className="text-base font-bold text-white">{item.name}</span>
                    <span className="display text-xl font-bold text-white">
                      {item.priceLabel}
                      {item.confirmed ? <span className="per"> / month</span> : null}
                    </span>
                  </span>
                  {item.description ? (
                    <span className="small" style={{ color: "#cbd5e1" }}>
                      {item.description}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
          <div className="opt" aria-disabled="true" style={{ opacity: 0.6, cursor: "default" }}>
            <span className="box" aria-hidden="true" />
            <span className="stack g8 flex-1">
              <span className="row justify-between gap-3">
                <span className="text-base font-bold text-white">Missed-call text-back</span>
                <span className="pill">Coming soon</span>
              </span>
              <span className="small">
                When you can&#8217;t pick up, the caller gets a text so the job doesn&#8217;t go elsewhere.
              </span>
            </span>
          </div>
        </div>
      </div>

      <aside
        className="panel topbar stack"
        aria-label="Order summary"
        style={{ flex: "1 1 340px", position: "sticky", top: 120, background: "#0f172a", padding: 28, gap: 18 }}
      >
        <h3 className="h4">Your order</h3>
        <div className="stack g8">
          <div className="row justify-between">
            <span className="body" style={{ fontSize: 15 }}>
              {chosen.name} site
            </span>
            <span className="ink font-semibold">{chosen.priceLabel}</span>
          </div>
          {addOn ? (
            <div className="row justify-between">
              <span className="body" style={{ fontSize: 15 }}>
                {addOn.name}, from launch
              </span>
              <span className="ink font-semibold">
                {addOn.confirmed ? `${addOn.priceLabel} / mo` : addOn.priceLabel}
              </span>
            </div>
          ) : null}
        </div>
        <hr className="divider" />
        <div className="stack g8">
          <span className="label" id="pay-option-label">
            How do you want to pay?
          </span>
          <div className="seg" role="group" aria-labelledby="pay-option-label">
            <button className={payFull ? "" : "on"} type="button" aria-pressed={!payFull} onClick={() => setPayFull(false)}>
              50% deposit
            </button>
            <button className={payFull ? "on" : ""} type="button" aria-pressed={payFull} onClick={() => setPayFull(true)}>
              Pay in full
            </button>
          </div>
        </div>
        <div className="row justify-between" style={{ alignItems: "baseline" }}>
          <span className="small">Due today</span>
          <span className="price">{dueLabel}</span>
        </div>
        <span className="caption">{note}</span>
        {apiReady && packageId ? (
          <>
            <CheckoutPayButton
              className="btn btn-primary btn-block"
              label={payLabel}
              analyticsParams={{
                product: chosen.key === "pro" ? "local_pro" : "local_starter",
                price: due ?? 0,
                value: due ?? 0,
                currency: "USD",
                payment_type: payFull ? "full" : "deposit",
                add_ons: addOn?.key ?? "",
              }}
              request={{
                product: "local",
                package: packageId,
                payment: payFull ? "full" : "deposit",
                addOn: addOnId,
                expectedAmount: due === null ? undefined : Math.round(due * 100),
              }}
            />
            <span className="caption">
              Secure checkout with Stripe. After paying, you&#8217;ll tell me about your business.{" "}
              <Link href="/refund-policy">Refund policy</Link>
            </span>
          </>
        ) : canCheckout && checkoutUrl ? (
          <>
            <a
              className="btn btn-primary btn-block"
              href={checkoutUrl}
              onClick={() =>
                track("checkout_click", {
                  product: chosen.key === "pro" ? "local_pro" : chosen.key === "starter" ? "local_starter" : `local_${chosen.key}`,
                  price: due ?? 0,
                  value: due ?? 0,
                  currency: "USD",
                  payment_type: payFull ? "full" : "deposit",
                  add_ons: addOn?.key ?? "",
                })
              }
            >
              {payLabel}
            </a>
            <span className="caption">
              Secure checkout with Stripe. After paying, you&#8217;ll tell me about your business.{" "}
              <Link href="/refund-policy">Refund policy</Link>
            </span>
          </>
        ) : (
          <>
            <button className="btn btn-block is-disabled" type="button" disabled>
              {payLabel}
            </button>
            <span className="caption">Checkout is unavailable right now. Send details or book a call and I&#8217;ll follow up.</span>
          </>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Link className="btn btn-outline" href={contactHref} style={{ padding: "10px 12px" }}>
            Send details
          </Link>
          <Link className="btn btn-outline" href={contactHref} style={{ padding: "10px 12px" }}>
            Book a call
          </Link>
        </div>
        <span className="caption">Totals are an estimate. The final amount is confirmed at checkout.</span>
      </aside>
    </div>
  );
}
